/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { ContentCopy } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'

import {
  getRenderedBoxRect,
  type NaturalImageSize,
} from './classificationUtils'
import {
  formatCreatedDate,
  formatToneLabel,
  formatUltraCompanyChoices,
  getExplorerBoxColor,
  getExplorerBoxTone,
  getImageToneCounts,
  toneStyles,
  type ExplorerProduct,
  type LabelingExplorerBox,
  type LabelingExplorerImage,
} from './explorerUtils'
import { BoxPredictionTooltip } from './explorerCards'

const ImageDetailDialog = ({
  image,
  productsByUpc,
  closeConfidenceThreshold,
  highlightedBoxId,
  openedFromCrop,
  onClose,
  onUseAsSearch,
}: {
  image: LabelingExplorerImage | null
  productsByUpc: Record<string, ExplorerProduct>
  closeConfidenceThreshold: number | null
  highlightedBoxId: string | null
  openedFromCrop: boolean
  onClose: () => void
  onUseAsSearch?: (box: LabelingExplorerBox) => void
}) => {
  const [naturalSize, setNaturalSize] = React.useState<NaturalImageSize | null>(
    null,
  )
  const [renderedSize, setRenderedSize] =
    React.useState<NaturalImageSize | null>(null)
  const imageRef = React.useRef<HTMLImageElement | null>(null)

  React.useEffect(() => {
    setNaturalSize(null)
    setRenderedSize(null)
  }, [image?.id])

  React.useEffect(() => {
    const renderedImage = imageRef.current
    if (!renderedImage) return

    const updateRenderedSize = () => {
      setRenderedSize({
        width: renderedImage.clientWidth,
        height: renderedImage.clientHeight,
      })
    }

    updateRenderedSize()
    const resizeObserver = new ResizeObserver(updateRenderedSize)
    resizeObserver.observe(renderedImage)
    return () => resizeObserver.disconnect()
  }, [image?.id, naturalSize])

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    setNaturalSize({
      width: event.currentTarget.naturalWidth,
      height: event.currentTarget.naturalHeight,
    })
    setRenderedSize({
      width: event.currentTarget.clientWidth,
      height: event.currentTarget.clientHeight,
    })
  }

  const copyText = (value: string) => void navigator.clipboard.writeText(value)
  const toneCounts = image
    ? getImageToneCounts(image, closeConfidenceThreshold)
    : null

  return (
    <Dialog open={Boolean(image)} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Image {image?.id}
          </Typography>
          {image && (
            <>
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={() => copyText(image.imageUrl)}
              >
                URL
              </Button>
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={() => copyText(image.id)}
              >
                ID
              </Button>
            </>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {image && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                label={formatUltraCompanyChoices(image.sourceCustomer)}
              />
              <Chip
                size="small"
                label={`Created ${formatCreatedDate(image.created)}`}
              />
              {!openedFromCrop && (
                <Chip size="small" label={`${image.boxCount} boxes`} />
              )}
              {toneCounts &&
                (
                  [
                    ['unidentified', toneCounts.unidentified],
                    ['close', toneCounts.close],
                    ['unverified', toneCounts.unverified],
                    ['verified', toneCounts.verified],
                  ] as const
                ).map(([tone, count]) => (
                  <Chip
                    key={tone}
                    size="small"
                    label={`${count} ${formatToneLabel(tone).toLowerCase()}`}
                    color={getExplorerBoxColor(tone)}
                    variant="outlined"
                  />
                ))}
            </Stack>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                minHeight: 0,
                overflow: 'hidden',
                bgcolor: 'grey.100',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  maxWidth: '100%',
                  maxHeight: '72vh',
                }}
              >
                <Box
                  component="img"
                  ref={imageRef}
                  src={image.imageUrl}
                  alt=""
                  onLoad={handleImageLoad}
                  sx={{
                    display: 'block',
                    width: 'auto',
                    maxWidth: '100%',
                    height: 'auto',
                    maxHeight: '72vh',
                    objectFit: 'contain',
                  }}
                />
                {naturalSize &&
                  renderedSize &&
                  (image.boundingBoxes ?? []).map(box => {
                    const tone = getExplorerBoxTone(
                      box,
                      closeConfidenceThreshold,
                    )
                    const style = toneStyles[tone]
                    const isHighlighted = box.id === highlightedBoxId
                    return (
                      <BoxPredictionTooltip
                        key={box.id}
                        box={box}
                        productsByUpc={productsByUpc}
                      >
                        <Box
                          onClick={
                            onUseAsSearch
                              ? () => {
                                  onUseAsSearch(box)
                                  onClose()
                                }
                              : undefined
                          }
                          sx={{
                            ...getRenderedBoxRect(
                              box,
                              naturalSize,
                              renderedSize,
                            ),
                            position: 'absolute',
                            border: isHighlighted
                              ? '4px solid #ff6b00'
                              : `2px solid ${style.border}`,
                            bgcolor: style.background,
                            borderRadius: 0.5,
                            cursor: onUseAsSearch ? 'pointer' : 'default',
                            zIndex: isHighlighted ? 2 : 1,
                            '&:hover': onUseAsSearch
                              ? { filter: 'brightness(1.4)' }
                              : undefined,
                          }}
                        />
                      </BoxPredictionTooltip>
                    )
                  })}
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ImageDetailDialog
